/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo } from "react";
import api from "../../../datasource/api";
import { useParams } from "react-router-dom";

interface PlanHistory {
    historyId: number;
    hospitalPlanId: number;

    oldProductId: number | null;
    newProductId: number | null;

    oldProductDescription?: string | null;
    newProductDescription?: string | null;

    oldHospitalId: string | null;
    newHospitalId: string | null;

    oldPaidDevicesCount: number | null;
    newPaidDevicesCount: number | null;

    oldPackageDeviceCount: number | null;
    newPackageDeviceCount: number | null;

    oldPlanTotalAmount: number | null;
    newPlanTotalAmount: number | null;

    oldServiceType: number | null;
    newServiceType: number | null;

    oldServiceTypeDescription?: string | null;
    newServiceTypeDescription?: string | null;

    oldPlanType: number | null;
    newPlanType: number | null;

    oldPlanTypeDescription?: string | null;
    newPlanTypeDescription?: string | null;

    oldPatientType: number | null;
    newPatientType: number | null;

    oldPatientTypeDescription?: string | null;
    newPatientTypeDescription?: string | null;

    oldInPatientAmount: number | null;
    newInPatientAmount: number | null;

    oldOutPatientAmount: number | null;
    newOutPatientAmount: number | null;

    oldValidityPeriod: number | null;
    newValidityPeriod: number | null;

    oldValidityPeriodDescription?: string | null;
    newvalidityPeriodDescription?: string | null;

    oldValidityStart: string | null;
    newValidityStart: string | null;

    oldValidityEnd: string | null;
    newValidityEnd: string | null;

    oldUpdatedDate: string | null;
    newUpdatedDate: string | null;

    oldUpdatedBy: string | null;
    newUpdatedBy: string | null;

    oldIsActive: boolean | null;
    newIsActive: boolean | null;

    oldIsDelete: boolean | null;
    newIsDelete: boolean | null;

    oldIsArchived: boolean | null;
    newIsArchived: boolean | null;

    changeTimestamp: string;
    changedBy: string;
    remark: string;

    customer: string;
    userName: string;
    role: string;
    userID: number;
    employeeID: string;
    photo: string;

    [key: string]: any;
}

interface SingleChange {
    field: string;
    oldVal: any;
    newVal: any;
}

const skipFields = ["oldHospitalId", "newHospitalId", "oldServiceType", "newServiceType", "oldPlanType", "newPlanType", "oldPatientType", "newPatientType", "oldValidityPeriod", "newValidityPeriod"];

const descriptionMap: Record<string, string> = {
    oldProductId: "oldProductDescription",
    newProductId: "newProductDescription",

    oldServiceType: "oldServiceTypeDescription",
    newServiceType: "newServiceTypeDescription",

    oldPlanType: "oldPlanTypeDescription",
    newPlanType: "newPlanTypeDescription",

    oldPatientType: "oldPatientTypeDescription",
    newPatientType: "newPatientTypeDescription",

    oldValidityPeriod: "oldValidityPeriodDescription",
    newValidityPeriod: "newvalidityPeriodDescription",
};

const beautifyField = (key: string): string =>
    key.replace("old", "").replace(/([A-Z])/g, " $1").trim();

const getActualValue = (row: any, key: string) => {
    const descKey = descriptionMap[key];
    return descKey && row[descKey] != null ? row[descKey] : row[key];
};

const getAllChanges = (row: PlanHistory): SingleChange[] => {
    const changes: SingleChange[] = [];
    const keys = Object.keys(row);

    for (const key of keys) {
        if (!key.startsWith("old")) continue;
        if (skipFields.includes(key)) continue;

        const newKey = key.replace("old", "new");
        if (!row.hasOwnProperty(newKey)) continue;

        const oldVal = getActualValue(row, key);
        const newVal = getActualValue(row, newKey);

        if (oldVal == null && newVal == null) continue;
        if (oldVal === newVal) continue;

        changes.push({
            field: beautifyField(key),
            oldVal,
            newVal,
        });
    }

    return changes;
};

const formatDate = (ts: string) =>
    new Date(ts).toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });

const EditHistory: React.FC = () => {
    const params = useParams();
    const [history, setHistory] = React.useState<PlanHistory[]>([]);

    const fetchHistory = async () => {
        const res = await api.get("/Hospital/hospitals/PlanHistory", {
            params: { hospitalId: params?.id },
        });
        setHistory(res.data);
    };

    useEffect(() => {
        if (params?.id) fetchHistory();
    }, [params?.id]);

    const parsed = useMemo(
        () =>
            history.map((row) => ({
                ...row,
                changes: getAllChanges(row),
            })),
        [history]
    );

    return (
        <div className="p-1 w-full pt-2">
            <div className="border rounded-lg overflow-hidden bg-white shadow-sm">
                <table className="min-w-full border-collapse">
                    <thead className="bg-gray-100">
                        <tr>
                            <th className="px-4 py-3 border text-left text-14 font-semibold min-w-[250px]">User</th>
                            <th className="px-4 py-3 border text-left text-14 font-semibold">Change</th>
                            <th className="px-4 py-3 border text-left text-14 font-semibold">Edited On</th>
                            <th className="px-4 py-3 border text-left text-14 font-semibold">Remark</th>
                        </tr>
                    </thead>

                    <tbody>
                        {parsed.map((row) => (
                            <tr key={row.historyId} className="hover:bg-gray-50">
                                {/* USER */}
                                <td className="px-4 py-3 border min-w-[250px]">
                                    <div className="flex items-center gap-3">
                                        <img
                                            src={row.photo}
                                            alt="User"
                                            className="w-10 h-10 rounded-full border object-cover"
                                        />
                                        <div>
                                            <div className="font-medium">{row.customer}</div>
                                            <div className="text-xs text-gray-500">
                                                ID: {row.employeeID} • {row.role}
                                            </div>
                                        </div>
                                    </div>
                                </td>

                                <td className=" border text-sm p-0">
                                    {row.changes.length > 0 ? (
                                        <ul className="space-y-0">
                                            {row.changes.map((chg, idx) => (
                                                <li className="px-4 py-3 border-b w-full" key={idx}>
                                                    <div className="font-semibold text-gray-800">
                                                        {chg.field}
                                                    </div>
                                                    <div>
                                                        <span className="text-red-600 font-semibold">From:</span>{" "}
                                                        {chg.oldVal ?? "-"}
                                                    </div>
                                                    <div>
                                                        <span className="text-green-700 font-semibold">To:</span>{" "}
                                                        {chg.newVal ?? "-"}
                                                    </div>
                                                </li>
                                            ))}
                                        </ul>
                                    ) : (
                                        <span className="text-gray-500">No change detected</span>
                                    )}
                                </td>

                                <td className="px-4 py-3 border text-sm text-gray-700">
                                    {formatDate(row.changeTimestamp)}
                                </td>

                                <td className="px-4 py-3 border text-sm text-gray-700">
                                    {row.remark || "-"}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default EditHistory;
